import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Building2, Plus, Users, UserCircle, Pencil, Upload, Check, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function OrgManagementPage() {
  const [orgs, setOrgs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editOrg, setEditOrg] = useState(null);
  const [form, setForm] = useState({ username: '', name: '', prescription_header: '' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [orgRes, userRes] = await Promise.all([
        fetch(`${API}/api/organisations`, { credentials: 'include' }),
        fetch(`${API}/api/admin/users`, { credentials: 'include' }),
      ]);
      if (orgRes.ok) setOrgs(await orgRes.json());
      if (userRes.ok) setUsers(await userRes.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async () => {
    if (!form.username.trim() || !form.name.trim()) return;
    try {
      const res = await fetch(`${API}/api/organisations`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) { setShowCreate(false); setForm({ username: '', name: '', prescription_header: '' }); fetchData(); }
      else { const d = await res.json(); alert(d.detail || 'Failed'); }
    } catch (e) { alert('Network error'); }
  };

  const handleUpdate = async () => {
    if (!editOrg) return;
    try {
      await fetch(`${API}/api/organisations/${editOrg.id}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: editOrg.name, prescription_header: editOrg.prescription_header }),
      });
      setEditOrg(null); fetchData();
    } catch (e) { alert('Network error'); }
  };

  const handleLogoUpload = async (orgId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    try {
      await fetch(`${API}/api/organisations/${orgId}/logo`, {
        method: 'POST', credentials: 'include', body: fd,
      });
      fetchData();
    } catch (e) { alert('Upload failed'); }
  };

  const handleAssignOrg = async (userId, orgId) => {
    try {
      await fetch(`${API}/api/admin/users/${userId}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ org_id: orgId }),
      });
      fetchData();
    } catch (e) { alert('Failed to assign org'); }
  };

  const handleToggleEnrich = async (userId, current) => {
    try {
      await fetch(`${API}/api/admin/users/${userId}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ can_enrich_medicines: !current }),
      });
      fetchData();
    } catch (e) { alert('Failed'); }
  };

  const nonAdminUsers = users.filter(u => u.role !== 'admin' && u.is_active);

  return (
    <div className="space-y-6 max-w-5xl" data-testid="org-management-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Organisation Management</h1>
          <p className="text-sm text-slate-500">Manage organisations, assign users, set permissions</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="bg-emerald-700 hover:bg-emerald-800" data-testid="create-org-btn">
          <Plus className="w-4 h-4 mr-1.5" /> New Organisation
        </Button>
      </div>

      {/* Orgs Grid */}
      <div className="grid gap-4" data-testid="org-list">
        {orgs.map(org => (
          <Card key={org.id} className="border-slate-200" data-testid={`org-card-${org.username}`}>
            <CardContent className="py-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  {org.logo_url ? (
                    <img src={`${API}${org.logo_url}`} alt="" className="w-12 h-12 rounded-xl object-cover border" />
                  ) : (
                    <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center border border-emerald-100">
                      <Building2 className="w-5 h-5 text-emerald-600" />
                    </div>
                  )}
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-slate-800">{org.name}</h3>
                      <Badge variant="outline" className="text-[9px] font-mono">{org.username}</Badge>
                    </div>
                    {org.prescription_header && org.prescription_header !== org.name && (
                      <p className="text-xs text-slate-500 mt-0.5">Rx header: {org.prescription_header}</p>
                    )}
                    <div className="flex gap-3 mt-1 text-[10px] text-slate-400">
                      <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {org.user_count} users</span>
                      <span className="flex items-center gap-1"><UserCircle className="w-3 h-3" /> {org.patient_count} patients</span>
                    </div>
                  </div>
                </div>
                <div className="flex gap-1.5">
                  <label className="cursor-pointer">
                    <input type="file" accept="image/*" className="hidden" onChange={e => e.target.files[0] && handleLogoUpload(org.id, e.target.files[0])} />
                    <Button variant="outline" size="sm" className="h-7 text-xs" asChild>
                      <span><Upload className="w-3 h-3 mr-1" /> Logo</span>
                    </Button>
                  </label>
                  <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setEditOrg({ ...org })} data-testid={`edit-org-${org.username}`}>
                    <Pencil className="w-3 h-3 mr-1" /> Edit
                  </Button>
                </div>
              </div>

              {/* Users in this org */}
              <div className="mt-3 pt-3 border-t border-slate-100">
                <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Assigned Users</p>
                <div className="flex flex-wrap gap-2">
                  {nonAdminUsers.filter(u => u.org_id === org.id).map(u => (
                    <div key={u.id} className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-slate-50 border text-xs">
                      <span className="font-medium text-slate-700">{u.name}</span>
                      <Badge className="text-[8px] bg-slate-200 text-slate-600">{u.role}</Badge>
                      {u.role === 'doctor' && (
                        <button
                          onClick={() => handleToggleEnrich(u.id, u.can_enrich_medicines)}
                          className={`ml-1 text-[8px] px-1.5 py-0.5 rounded-full border ${u.can_enrich_medicines ? 'bg-emerald-50 border-emerald-200 text-emerald-600' : 'bg-slate-50 border-slate-200 text-slate-400'}`}
                          title={u.can_enrich_medicines ? 'Can enrich medicines (click to revoke)' : 'Cannot enrich medicines (click to grant)'}
                          data-testid={`enrich-toggle-${u.id}`}
                        >
                          {u.can_enrich_medicines ? <Check className="w-2.5 h-2.5 inline" /> : <X className="w-2.5 h-2.5 inline" />} Enrich
                        </button>
                      )}
                    </div>
                  ))}
                  {nonAdminUsers.filter(u => u.org_id === org.id).length === 0 && (
                    <p className="text-xs text-slate-400">No users assigned</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Unassigned Users */}
      {nonAdminUsers.filter(u => !u.org_id).length > 0 && (
        <Card className="border-amber-200 bg-amber-50/30" data-testid="unassigned-users">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-amber-700">Unassigned Users</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {nonAdminUsers.filter(u => !u.org_id).map(u => (
                <div key={u.id} className="flex items-center justify-between p-2 rounded-lg bg-white border">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{u.name}</span>
                    <Badge className="text-[9px]">{u.role}</Badge>
                  </div>
                  <select
                    className="text-xs border rounded px-2 py-1"
                    defaultValue=""
                    onChange={e => e.target.value && handleAssignOrg(u.id, e.target.value)}
                    data-testid={`assign-org-${u.id}`}
                  >
                    <option value="">Assign to org...</option>
                    {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                  </select>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create Org Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create Organisation</DialogTitle>
            <DialogDescription>Add a new organisation to the system.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div>
              <label className="text-xs font-medium text-slate-600">Username (unique, lowercase)</label>
              <Input
                value={form.username}
                onChange={e => setForm(p => ({ ...p, username: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }))}
                placeholder="e.g. skin-plus-clinic"
                data-testid="org-username-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Display Name</label>
              <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Skin Plus Clinic" data-testid="org-name-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Prescription Header (shown on PDF)</label>
              <Input value={form.prescription_header} onChange={e => setForm(p => ({ ...p, prescription_header: e.target.value }))} placeholder="Skin Plus Clinic - Dermatology Centre" data-testid="org-header-input" />
            </div>
            <Button onClick={handleCreate} className="w-full bg-emerald-700" data-testid="create-org-submit">Create Organisation</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Org Dialog */}
      <Dialog open={!!editOrg} onOpenChange={v => !v && setEditOrg(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Organisation</DialogTitle>
            <DialogDescription>Update organisation details.</DialogDescription>
          </DialogHeader>
          {editOrg && (
            <div className="space-y-3 pt-2">
              <div>
                <label className="text-xs font-medium text-slate-600">Username</label>
                <Input value={editOrg.username} disabled className="bg-slate-50" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Display Name</label>
                <Input value={editOrg.name} onChange={e => setEditOrg(p => ({ ...p, name: e.target.value }))} data-testid="edit-org-name" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Prescription Header</label>
                <Input value={editOrg.prescription_header || ''} onChange={e => setEditOrg(p => ({ ...p, prescription_header: e.target.value }))} data-testid="edit-org-header" />
              </div>
              <Button onClick={handleUpdate} className="w-full bg-emerald-700" data-testid="save-org-btn">Save Changes</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
